/**
 * Response processor.
 *
 * Turns a FetchResult into a completed navigation. The two arms
 * of the discriminated union map to the two flows:
 *
 * - 'partial': JSON envelope from SpaNavigationSubscriber or a
 *   DataProvider controller. No HTML parsing, no content swap.
 *   drupalSettings is merged, assets listed in the envelope are
 *   loaded, and a normalized PageData payload is handed back to
 *   the controller for state-store updates and event dispatch.
 *
 * - 'html': full-response path for pages that fall outside the
 *   SDX engine. The parser pulls out the content region, new
 *   assets, and component IDs; the content swapper reconciles
 *   the DOM; behaviors detach/attach; the controller finishes
 *   up the state-store update.
 *
 * Both flows short-circuit to `window.location.replace(url)` on
 * a theme mismatch: if the current active theme differs from the
 * target theme, the shells are structurally incompatible (region
 * layout, attached libraries, CSS) and no partial swap can
 * safely reconcile them.
 */

import { currentActiveTheme } from './theme';
import { mergeDrupalSettings } from '@sdx/core/drupal-settings';
import { parsePage } from './parse-page';
import { swapContent } from './content-swapper';
import { loadAssets } from './asset-loader';
import { updateActiveLinks } from './active-links';
import { logger } from './logger';
import type {
  NavScope,
  PageData,
  ParsedPage,
  PartialResponseEnvelope,
} from './types';
import { NAV_EVENT } from './types';
import type { AssetInventory } from './asset-inventory';
import type { ScriptRef } from './asset-loader';
import type { ComponentResolverRegistry } from './component-resolver';
import type { TagStore } from './tag-store';
import type { RegionChecksums } from './region-checksums';

export interface ResponseProcessorContext {
  readonly contentSelector: string;
  readonly devMode: boolean;
  readonly inventory: AssetInventory;
  readonly componentResolvers: ComponentResolverRegistry;
  readonly tagStore: TagStore;
  readonly regionChecksums: RegionChecksums;
  /** Current menu hash, updated from every partial response. */
  menuHash: string;
  /** Current route name, updated from every partial response. */
  routeName: string;
}

export type ProcessOutcome =
  | { kind: 'completed'; pageData: PageData }
  | { kind: 'full-reload'; reason: 'theme-mismatch' | 'swap-failed' | 'confirm-form' }
  | { kind: 'handled-out-of-band'; reason: 'confirm-modal' };

export async function processPartialResponse(
  envelope: PartialResponseEnvelope,
  url: string,
  scope: NavScope,
  ctx: ResponseProcessorContext,
): Promise<ProcessOutcome> {
  if (envelope.menuHash) ctx.menuHash = envelope.menuHash;
  if (envelope.route) ctx.routeName = envelope.route;

  mergeRegionChecksums(envelope, ctx.regionChecksums);
  maintainTagStore(envelope, ctx.tagStore);

  if (hasThemeMismatch(envelope.activeTheme ?? null)) {
    return { kind: 'full-reload', reason: 'theme-mismatch' };
  }

  if (envelope.confirm) {
    const handled = !document.dispatchEvent(
      new CustomEvent(NAV_EVENT.CONFIRM_MODAL, { detail: envelope, cancelable: true }),
    );
    if (handled) {
      history.back();
      return { kind: 'handled-out-of-band', reason: 'confirm-modal' };
    }
    return { kind: 'full-reload', reason: 'confirm-form' };
  }

  const pageData = buildPageDataFromEnvelope(envelope, scope);

  updateDocumentTitle(envelope);

  if (envelope.drupalSettings) {
    mergeDrupalSettings(
      drupalSettings as unknown as Record<string, unknown>,
      envelope.drupalSettings,
    );
  }

  const plan = collectAssetPlanFromEnvelope(envelope, ctx.inventory);
  await loadAssets(plan, ctx.inventory, ctx.componentResolvers);

  updateActiveLinks(url);
  if (pageData.scope !== 'content') {
    window.scrollTo(0, 0);
  }

  return { kind: 'completed', pageData };
}

export async function processHtmlResponse(
  html: string,
  url: string,
  ctx: ResponseProcessorContext,
): Promise<ProcessOutcome> {
  const parsed: ParsedPage = parsePage(html, ctx.contentSelector, ctx.inventory);

  const targetTheme = extractParsedTheme(parsed.settings);
  if (hasThemeMismatch(targetTheme)) {
    return { kind: 'full-reload', reason: 'theme-mismatch' };
  }

  // Merge the target page's drupalSettings BEFORE loading assets, exactly
  // like the envelope path does: the component preload resolves bundle
  // URLs through the framework's settings map, and against the previous
  // page's map every component new to this page misses, falls back to the
  // conventional unhashed filename, and 404s. The merge also means the
  // attachBehaviors call after the swap sees the target page's settings
  // instead of the ones this navigation just replaced.
  if (parsed.settings) {
    mergeDrupalSettings(
      drupalSettings as unknown as Record<string, unknown>,
      parsed.settings,
    );
  }

  await loadAssets(parsed, ctx.inventory, ctx.componentResolvers);

  const result = swapContent(parsed, {
    contentSelector: ctx.contentSelector,
    devMode: ctx.devMode,
  });

  if (!result.swapped) {
    return { kind: 'full-reload', reason: 'swap-failed' };
  }

  const pageData: PageData = result.sdxPageData
    ? (result.sdxPageData as PageData)
    : { title: parsed.title ?? undefined };

  // Re-attach Drupal behaviors after the swap. Two
  // requestAnimationFrame hops wait for the framework's commit
  // phase so attachBehaviors finds every new mount point.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        Drupal.attachBehaviors(document.body, drupalSettings);
      }
      catch (err) {
        logger.warn('attachBehaviors after HTML swap threw.', err);
      }
    });
  });

  updateActiveLinks(url);
  scrollToHashOrTop(url);

  return { kind: 'completed', pageData };
}

function buildPageDataFromEnvelope(
  envelope: PartialResponseEnvelope,
  scope: NavScope,
): PageData {
  let pageData: PageData;

  if (envelope.component && envelope.props !== undefined) {
    pageData = {
      _dataProvider: true,
      component: envelope.component,
      props: envelope.props,
      _manifest: envelope._manifest ?? {},
      _dependencyMap: envelope._dependencyMap ?? {},
      _tagChecksums: envelope._tagChecksums ?? {},
      _deferred: envelope._deferred ?? [],
      _lazy: envelope._lazy ?? [],
      _layout: envelope.layout ?? null,
      csrfToken: envelope.csrfToken ?? '',
      route: envelope.route ?? '',
      contextualLinks: envelope.contextualLinks ?? [],
    };
    if (envelope.shell) {
      for (const [key, value] of Object.entries(envelope.shell)) {
        pageData[key] = value;
      }
    }
  }
  else {
    pageData = (envelope.pageData ?? envelope) as PageData;
  }

  pageData.scope = envelope.scope ?? scope ?? 'full';
  return pageData;
}

function collectAssetPlanFromEnvelope(
  envelope: PartialResponseEnvelope,
  inventory: AssetInventory,
): { newStyles: string[]; newScripts: ScriptRef[] } {
  const plan = { newStyles: [] as string[], newScripts: [] as ScriptRef[] };

  for (const href of envelope.styles ?? []) {
    const absolute = absolutize(href);
    if (!inventory.hasStylesheet(absolute)) {
      plan.newStyles.push(absolute);
    }
  }
  // Entries are plain URLs for classic scripts or {src, type} objects
  // for ES modules; the type must survive to the injected tag or the
  // module chunk is evaluated as a classic script and crashes.
  for (const entry of envelope.scripts ?? []) {
    const src = typeof entry === 'string' ? entry : entry.src;
    if (!src) continue;
    const absolute = absolutize(src);
    if (!inventory.hasScript(absolute)) {
      const type = typeof entry === 'string' ? undefined : entry.type;
      plan.newScripts.push(type ? { src: absolute, type } : { src: absolute });
    }
  }
  return plan;
}

function mergeRegionChecksums(
  envelope: PartialResponseEnvelope,
  regionChecksums: RegionChecksums,
): void {
  const pageData = (envelope.pageData ?? envelope) as { _regionChecksums?: Record<string, string> };
  if (pageData._regionChecksums) {
    regionChecksums.merge(pageData._regionChecksums);
  }
}

function maintainTagStore(envelope: PartialResponseEnvelope, tagStore: TagStore): void {
  if (envelope.component && envelope._manifest) {
    tagStore.update(envelope);
  }
  else if (!envelope.component) {
    tagStore.clear();
  }
}

function updateDocumentTitle(envelope: PartialResponseEnvelope): void {
  const shellTitle = (envelope.shell as { title?: unknown } | undefined)?.title;
  const title = typeof shellTitle === 'string' ? shellTitle : envelope.title;
  if (typeof title === 'string' && title.length > 0) {
    document.title = title;
  }
}

function hasThemeMismatch(targetTheme: string | null): boolean {
  return currentActiveTheme() !== targetTheme;
}

function extractParsedTheme(settings: Record<string, unknown> | null): string | null {
  if (!settings) return null;
  const sdx = settings.sdx as { activeTheme?: unknown } | undefined;
  const theme = sdx?.activeTheme;
  return typeof theme === 'string' && theme.length > 0 ? theme : null;
}

function absolutize(url: string): string {
  try {
    return new URL(url, location.origin).href;
  }
  catch {
    return url;
  }
}

function scrollToHashOrTop(url: string): void {
  let hash = '';
  try {
    hash = new URL(url, location.origin).hash;
  }
  catch {
    hash = '';
  }
  if (hash) {
    const target = document.querySelector(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
      return;
    }
  }
  window.scrollTo(0, 0);
}
